import type { PersistentUserSession } from '@mimir/client-common/util/persistentUserSession';
import type { ServiceCache } from '@mimir/common/client/serviceCache';
import type { Route } from 'vue-router/types/router';
import type { StoreType } from './state';
import { isClientConfig } from '@mimir/common/dto/ClientConfigDto';
import type { AppRoutingState } from '../sharedState/appRoutingState';
import { initializeGlobalBackendClient } from '@mimir/client-common/globals/initializeGlobalBackendClient';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import { updateGoogleMapPromise } from '../globals/variables';
import type { ItemMediaPlayerOps } from '@mimir/web/model/itemMediaPlayerOps';

export async function initializeStore(
  serviceCache: ServiceCache,
  session: PersistentUserSession,
  store: StoreType,
  appRoutingState: AppRoutingState,
  destinationRoute: Route,
  applicationContext: ApplicationContext,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
) {
  /*
    We need to initialize and bootstrap the store and other global state, so
    that among other things the client configuration is available, such that
    things like the static API layer that poke global state through config
    getGlobalServiceDocument() will work.
  */
  await initialize(serviceCache, store);
  const bootstrap = await import('../bootstrap');
  await bootstrap.bootstrap(quickPreviewMediaPlayerOps, applicationContext, session, store, destinationRoute);

  appRoutingState.storeInitialized = true;
}

async function loadGoogleMap(apiKey: string | null, store: StoreType) {

  try {
    if (!apiKey) return;
    //If google is blocked, then we can't load the library
    const { Loader } = await import('@googlemaps/js-api-loader');
    const loader = new Loader({
      apiKey,
      libraries: ['places']
    });
    await loader.load();
  } catch (err) {
    console.error('Error loading Google Maps API', err);
  }
}

const initialize = async (serviceCache: ServiceCache, store: StoreType) => {
  const serviceDocument = await serviceCache.getServiceDocument();

  if (!serviceDocument) {
    throw new Error('No service document found.');
  }
  const clientConfig = serviceDocument.clientConfig;
  if (!isClientConfig(clientConfig)) {
    throw new Error('No clientConfig found.');
  }

  initializeGlobalBackendClient(serviceDocument);

  updateGoogleMapPromise(loadGoogleMap(clientConfig.googleMapsApiKey, store));
};
